/**
 * rn-membership-perks.js
 * Handles:
 * - Desktop: vendor logo grid → detail panel switching
 * - Mobile: sticky strip collapse on scroll, bottom sheet open/close
 * - CSV code fetching from Shopify CDN (unique codes)
 * - Static code reveal
 * - Email dispatch (unique code emailed via Apps Script, token authenticated,
 *   real JSON response read and mapped to honest member-facing status messages)
 * - Copy to clipboard with feedback
 * - Member ID reveal (Appstle) + copy, GA4 page-scoped tracking
 */
(function () {
  'use strict';

  var FEEDBACK_DURATION = 2000;
  var STRIP_COLLAPSE_THRESHOLD = 60;

  /* ── State ── */
  var codeCache = {};

  /* ── Helpers ── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function slugify(s) {
    return String(s).toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function trackEvent(name, params) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params || {});
    }
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); resolve(); }
      catch (err) { reject(err); }
      finally { document.body.removeChild(ta); }
    });
  }

  /* ── CSV Fetching ── */
  function fetchCSVCodes(url) {
    if (codeCache[url]) return Promise.resolve(codeCache[url]);

    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('CSV fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var codes = text
          .split('\n')
          .map(function (line) { return line.trim(); })
          .filter(function (line) { return line && line.length > 0 && line.toUpperCase() !== 'CODE'; });
        codeCache[url] = codes;
        return codes;
      });
  }

  function getNextCode(vendorKey, codes) {
    var lastCode = null;
    try {
      lastCode = localStorage.getItem('rn-last-' + vendorKey);
    } catch (e) { /* localStorage unavailable */ }

    /* Pick a random code that isn't the last one shown */
    var pool = codes.length > 1
      ? codes.filter(function (c) { return c !== lastCode; })
      : codes;
    var code = pool[Math.floor(Math.random() * pool.length)];

    try {
      localStorage.setItem('rn-last-' + vendorKey, code);
    } catch (e) { /* localStorage unavailable */ }

    return code;
  }

  /* ── Vendor Switching (Desktop) ── */
  function activateVendor(vendorId) {
    /* Update logo grid active state */
    qsa('[data-vendor-trigger]').forEach(function (btn) {
      var isTarget = btn.getAttribute('data-vendor-id') === vendorId;
      btn.classList.toggle('rn-membership-perks__logo-card--active', isTarget);
      btn.classList.toggle('rn-membership-perks__mobile-logo--active', isTarget);
    });

    /* Show correct detail panel */
    qsa('[data-vendor-panel]').forEach(function (panel) {
      var isTarget = panel.getAttribute('data-vendor-panel') === vendorId;
      panel.classList.toggle('rn-membership-perks__detail-card--active', isTarget);
    });
  }

  /* ── Reveal Code ── */
  function revealCode(btn) {
    var card = btn.closest('.rn-membership-perks__detail-card') ||
               btn.closest('.rn-membership-perks__sheet-content');
    if (!card) return;

    var csvUrl = btn.getAttribute('data-csv-url');
    var vendorKey = btn.getAttribute('data-vendor-key');

    if (csvUrl) {
      btn.textContent = 'Loading...';
      btn.disabled = true;

      fetchCSVCodes(csvUrl)
        .then(function (codes) {
          if (!codes.length) {
            btn.textContent = 'No codes available';
            return;
          }
          var code = getNextCode(vendorKey, codes);
          var codeEl = qs('[data-code-value]', card);
          if (codeEl) codeEl.textContent = code;

          card.classList.add('rn-membership-perks__detail-card--revealed');

          /* Show code area */
          var wrap = qs('[data-code-wrap]', card);
          if (wrap) wrap.removeAttribute('aria-hidden');

          /* Update button for re-reveal */
          btn.textContent = 'Generate New Code';
          btn.disabled = false;

          trackEvent('vendor_code_revealed', { vendor_name: vendorKey || '' });
        })
        .catch(function (err) {
          console.warn('[RN Perks] CSV fetch error:', err);
          btn.textContent = 'Error loading codes';
          btn.disabled = false;
        });
    } else {
      /* Static code — just reveal, hide button */
      card.classList.add('rn-membership-perks__detail-card--revealed');
      btn.style.display = 'none';

      var wrap = qs('[data-code-wrap]', card);
      if (wrap) wrap.removeAttribute('aria-hidden');

      trackEvent('vendor_code_revealed', { vendor_name: vendorKey || '' });
    }
  }

  /* ── Email Dispatch (unique code via Apps Script) ── */
  function dispatchKey(vendor) { return 'rn-perk-sent-' + slugify(vendor); }

  function markDispatchSent(btn, vendor) {
    btn.disabled = true;
    btn.classList.add('rn-membership-perks__dispatch-btn--sent');
    btn.textContent = 'Sent \u2713';
    var status = qs('[data-dispatch-status]', btn.parentNode);
    if (status) status.textContent = 'Check your inbox for your ' + vendor + ' code.';
  }

  /* Map server error codes to honest member-facing messages */
  function dispatchErrorMessage(code) {
    switch (code) {
      case 'pool_empty':
        return 'We are out of codes for this vendor right now. The team has been alerted and more are on the way.';
      case 'vendor_not_found':
        return 'This perk is not set up yet. Please check back soon.';
      case 'unauthorized':
        return 'This perk is temporarily unavailable. Please try again later.';
      case 'busy_try_again':
        return 'High demand right now. Please try again in a moment.';
      case 'missing_vendor_or_email':
        return 'Something went wrong. Please refresh the page and try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  function failDispatch(btn, status, originalLabel, errorCode, vendor) {
    btn.disabled = false;
    btn.textContent = originalLabel;
    if (status) status.textContent = dispatchErrorMessage(errorCode);
    trackEvent('vendor_code_email_failed', {
      vendor_name: vendor,
      error_code: errorCode || 'unknown'
    });
  }

  function handleDispatch(btn) {
    var url = btn.getAttribute('data-dispatcher-url');
    var vendor = btn.getAttribute('data-vendor-name') || '';
    var email = btn.getAttribute('data-customer-email') || '';
    var token = btn.getAttribute('data-dispatch-token') || '';
    var status = qs('[data-dispatch-status]', btn.parentNode);

    if (!url || !email) {
      if (status) {
        status.textContent = email
          ? 'This perk is not set up yet. Please check back soon.'
          : 'Log in to your member account to receive your code.';
      }
      return;
    }

    btn.disabled = true;
    var original = btn.textContent;
    btn.textContent = 'Sending\u2026';
    if (status) status.textContent = '';

    var params = new URLSearchParams();
    params.set('vendor', vendor);
    params.set('email', email);
    if (token) params.set('token', token);

    var requestUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + params.toString();

    /* GET with redirect: 'follow' so we cross the Apps Script 302 to
       googleusercontent.com and read the real JSON response. No more
       fire-and-forget false positives. */
    fetch(requestUrl, { method: 'GET', redirect: 'follow' })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.ok) {
          try { sessionStorage.setItem(dispatchKey(vendor), '1'); } catch (e) {}
          markDispatchSent(btn, vendor);
          trackEvent('vendor_code_emailed', { vendor_name: vendor });
        } else {
          failDispatch(btn, status, original, data && data.error, vendor);
        }
      })
      .catch(function (err) {
        console.warn('[RN Perks] dispatch error:', err);
        failDispatch(btn, status, original, 'network', vendor);
      });
  }

  /* Restore the "sent" state on already-emailed vendors (desktop + cloned sheet) */
  function applyDispatchSentState(scope) {
    qsa('[data-dispatch-trigger]', scope).forEach(function (btn) {
      var vendor = btn.getAttribute('data-vendor-name') || '';
      var sent = false;
      try { sent = sessionStorage.getItem(dispatchKey(vendor)) === '1'; } catch (e) {}
      if (sent) markDispatchSent(btn, vendor);
    });
  }

/* ── Member Product Offer: one tap add to cart ──
     Pricing is handled entirely by Shopify's automatic discount, so this
     only ever adds the line and moves the member along. */
  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function handleCartAdd(btn) {
    if (btn.disabled) return;

    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    var after  = btn.getAttribute('data-after-add') || 'cart';
    var vendor = btn.getAttribute('data-vendor-name') || '';
    var card   = btn.closest('.rn-membership-perks__detail-card') ||
                 btn.closest('.rn-membership-perks__sheet-content');
    var status = card ? qs('[data-cart-status]', card) : null;
    var original = btn.textContent;
    var root = shopifyRoot();

    btn.disabled = true;
    btn.textContent = 'Adding\u2026';
    if (status) status.textContent = '';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (d) {
            throw new Error(d.description || d.message || 'Add to cart failed');
          });
        }
        return res.json();
      })
      .then(function () {
        trackEvent('vendor_product_added', {
          vendor_name: vendor,
          product_title: btn.getAttribute('data-product-title') || '',
          variant_id: variantId
        });

        if (after === 'cart') {
          window.location.href = root + 'cart';
          return;
        }
        if (after === 'checkout') {
          window.location.href = '/checkout';
          return;
        }

        btn.textContent = 'Added \u2713';
        if (status) status.textContent = 'Added to your cart.';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = original;
        }, FEEDBACK_DURATION * 2);
      })
      .catch(function (err) {
        console.warn('[RN Perks] cart add error:', err);
        btn.disabled = false;
        btn.textContent = original;
        if (status) status.textContent = 'Could not add to cart. Please try again.';
      });
  }

  /* ── Copy Code ── */
  function handleCopy(btn) {
    var card = btn.closest('.rn-membership-perks__detail-card') ||
               btn.closest('.rn-membership-perks__sheet-content');
    if (!card) return;

    var codeEl = qs('[data-code-value]', card);
    if (!codeEl) return;

    var code = codeEl.textContent.trim();
    if (!code) return;

    copyToClipboard(code).then(function () {
      btn.textContent = 'Copied!';
      var feedback = qs('[data-copy-feedback]', card);
      if (feedback) feedback.textContent = 'Copied!';

      var keyEl = qs('[data-vendor-key]', card);
      var vendorKey = keyEl ? keyEl.getAttribute('data-vendor-key') : '';
      trackEvent('vendor_code_copied', { vendor_name: vendorKey || '' });

      setTimeout(function () {
        btn.textContent = 'Copy';
        if (feedback) feedback.textContent = '';
      }, FEEDBACK_DURATION);
    }).catch(function () {
      btn.textContent = 'Failed';
      setTimeout(function () { btn.textContent = 'Copy'; }, FEEDBACK_DURATION);
    });
  }

  /* ── Member ID (Appstle) ── */
  var MEMBER_ID_REVEALED = 'rn-membership-perks__member-id--revealed';
  var MEMBER_ID_NOANIM = 'rn-membership-perks__member-id--no-anim';

  function setupMemberId() {
    var wrap = qs('[data-member-id-wrap]');
    if (!wrap) return;

    /* GA4 user_id scoped to this page only (element only exists here) */
    var customerId = wrap.getAttribute('data-customer-id');
    if (customerId && typeof window.gtag === 'function') {
      window.gtag('set', { 'user_id': customerId });
    }

    var cover = qs('[data-member-id-reveal]', wrap);
    if (cover) initMemberIdSlide(wrap, cover);

    /* Restore revealed state for this session, without animating */
    try {
      if (sessionStorage.getItem('rnMemberIdRevealed') === '1') {
        wrap.classList.add(MEMBER_ID_NOANIM, MEMBER_ID_REVEALED);
        if (cover) {
          cover.style.transform = 'translateX(-100%)';
          cover.style.opacity = '0';
          cover.style.pointerEvents = 'none';
        }
        void wrap.offsetWidth; /* force reflow so the no-anim state applies */
        wrap.classList.remove(MEMBER_ID_NOANIM);
      }
    } catch (e) { /* sessionStorage unavailable */ }
  }

  function revealMemberId(wrap, cover) {
    if (wrap.classList.contains(MEMBER_ID_REVEALED)) return;
    if (cover) {
      cover.style.transition = '';
      cover.style.transform = 'translateX(-100%)';
      cover.style.opacity = '0';
      cover.style.pointerEvents = 'none';
    }
    wrap.classList.add(MEMBER_ID_REVEALED);
    try { sessionStorage.setItem('rnMemberIdRevealed', '1'); } catch (e) {}
    trackEvent('membership_id_revealed');
  }

  function initMemberIdSlide(wrap, cover) {
    var dragging = false, startX = 0, dx = 0, width = 0, moved = false;

    function isRevealed() { return wrap.classList.contains(MEMBER_ID_REVEALED); }
    function pointerX(e) { return e.touches ? e.touches[0].clientX : e.clientX; }

    function down(e) {
      if (isRevealed()) return;
      dragging = true;
      moved = false;
      dx = 0;
      width = cover.getBoundingClientRect().width || wrap.getBoundingClientRect().width;
      startX = pointerX(e);
      cover.style.transition = 'none';
    }

    function move(e) {
      if (!dragging) return;
      dx = Math.min(0, pointerX(e) - startX);
      if (Math.abs(dx) > 3) moved = true;
      cover.style.transform = 'translateX(' + dx + 'px)';
    }

    function up() {
      if (!dragging) return;
      dragging = false;
      cover.style.transition = '';
      if (!moved) {
        revealMemberId(wrap, cover);          /* treat as a tap */
      } else if (dx < -width * 0.4) {
        revealMemberId(wrap, cover);          /* dragged past threshold */
      } else {
        cover.style.transform = 'translateX(0)'; /* snap back */
        dx = 0;
      }
    }

    cover.addEventListener('mousedown', down);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    cover.addEventListener('touchstart', down, { passive: true });
    document.addEventListener('touchmove', move, { passive: true });
    document.addEventListener('touchend', up);

    /* Keyboard activation (Enter/Space) fires click with no drag */
    cover.addEventListener('click', function () {
      if (moved) { moved = false; return; }
      if (!dragging) revealMemberId(wrap, cover);
    });
  }

  function copyMemberId(btn) {
    var wrap = btn.closest('[data-member-id-wrap]');
    if (!wrap) return;
    var valueEl = qs('[data-member-id-value]', wrap);
    if (!valueEl) return;
    var value = valueEl.textContent.trim();
    if (!value) return;

    copyToClipboard(value).then(function () {
      btn.textContent = 'Copied!';
      var feedback = qs('[data-member-id-feedback]', wrap);
      if (feedback) feedback.textContent = 'Copied!';
      trackEvent('membership_id_copied');
      setTimeout(function () {
        btn.textContent = 'Copy';
        if (feedback) feedback.textContent = '';
      }, FEEDBACK_DURATION);
    }).catch(function () {
      btn.textContent = 'Failed';
      setTimeout(function () { btn.textContent = 'Copy'; }, FEEDBACK_DURATION);
    });
  }

  /* ── Mobile Bottom Sheet ── */
  function openSheet(vendorId) {
    var backdrop = qs('[data-sheet-backdrop]');
    var sheet = qs('[data-sheet]');
    var content = qs('[data-sheet-content]');
    if (!backdrop || !sheet || !content) return;

    /* Clone the detail card content into the sheet */
    var panel = qs('[data-vendor-panel="' + vendorId + '"]');
    if (!panel) return;

    content.innerHTML = panel.innerHTML;

    /* Re-apply email-sent state to the cloned dispatch button */
    applyDispatchSentState(content);

    backdrop.classList.add('rn-membership-perks__sheet-backdrop--active');
    backdrop.setAttribute('aria-hidden', 'false');
    sheet.classList.add('rn-membership-perks__sheet--active');
    sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeSheet() {
    var backdrop = qs('[data-sheet-backdrop]');
    var sheet = qs('[data-sheet]');
    if (!backdrop || !sheet) return;

    sheet.classList.remove('rn-membership-perks__sheet--active');
    sheet.setAttribute('aria-hidden', 'true');
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    setTimeout(function () {
      backdrop.classList.remove('rn-membership-perks__sheet-backdrop--active');
      var content = qs('[data-sheet-content]');
      if (content) content.innerHTML = '';
    }, 350);
  }

  /* ── Mobile Tap Hint ── */
  function dismissStripHint() {
    var hint = qs('[data-strip-hint]');
    if (!hint || hint.classList.contains('rn-membership-perks__mobile-hint--dismissed')) return;
    hint.classList.add('rn-membership-perks__mobile-hint--dismissed');
    try { sessionStorage.setItem('rnPerksHintDismissed', '1'); } catch (err) {}
  }

  function setupStripHint() {
    var hint = qs('[data-strip-hint]');
    if (!hint) return;
    try {
      if (sessionStorage.getItem('rnPerksHintDismissed') === '1') {
        hint.classList.add('rn-membership-perks__mobile-hint--dismissed');
      }
    } catch (err) {}
  }

  /* ── Mobile Sticky Strip Collapse ── */
  function setupStickyCollapse() {
    var strip = qs('.rn-membership-perks__mobile-strip');
    if (!strip) return;

    var lastY = 0;
    var collapsed = false;

    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      if (y > STRIP_COLLAPSE_THRESHOLD && !collapsed) {
        strip.classList.add('rn-membership-perks__mobile-strip--collapsed');
        collapsed = true;
      } else if (y <= STRIP_COLLAPSE_THRESHOLD && collapsed) {
        strip.classList.remove('rn-membership-perks__mobile-strip--collapsed');
        collapsed = false;
      }
      lastY = y;
    }, { passive: true });
  }

  /* ── Mobile Infinite Loop Strip ── */
  function setupInfiniteLoop() {
    var scroller = qs('.rn-membership-perks__mobile-strip-scroll');
    if (!scroller) return;

    var setWidth = 0;
    var built = false;
    var jumping = false;
    var resizeTimer = null;

    function getOriginals() {
      return qsa('.rn-membership-perks__mobile-logo', scroller).filter(function (el) {
        return !el.hasAttribute('data-clone');
      });
    }

    function measure(originals) {
      var styles = window.getComputedStyle(scroller);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      var w = 0;
      originals.forEach(function (el) {
        w += el.getBoundingClientRect().width + gap;
      });
      return w;
    }

    function build(originals) {
      var lead = document.createDocumentFragment();
      var trail = document.createDocumentFragment();
      originals.forEach(function (el) {
        [el.cloneNode(true), el.cloneNode(true)].forEach(function (clone, i) {
          clone.setAttribute('data-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('tabindex', '-1');
          clone.classList.remove('rn-membership-perks__mobile-logo--active');
          (i === 0 ? lead : trail).appendChild(clone);
        });
      });
      scroller.insertBefore(lead, scroller.firstChild);
      scroller.appendChild(trail);
    }

    function recenter() {
      setWidth = measure(getOriginals());
      if (setWidth > 0) scroller.scrollLeft = setWidth;
    }

    function tryBuild() {
      if (built || !isMobile()) return;
      var originals = getOriginals();
      if (originals.length < 2) return;
      /* Only loop when the set actually overflows the viewport */
      if (measure(originals) <= scroller.clientWidth) return;
      build(originals);
      built = true;
      recenter();
    }

    scroller.addEventListener('scroll', function () {
      if (!built || jumping || setWidth <= 0) return;
      var x = scroller.scrollLeft;
      if (x < setWidth * 0.5) {
        jumping = true;
        scroller.scrollLeft = x + setWidth;
        requestAnimationFrame(function () { jumping = false; });
      } else if (x > setWidth * 1.5) {
        jumping = true;
        scroller.scrollLeft = x - setWidth;
        requestAnimationFrame(function () { jumping = false; });
      }
    }, { passive: true });

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (!built) { tryBuild(); } else { recenter(); }
      }, 200);
    }, { passive: true });

    tryBuild();
  }

  /* ── Detect mobile ── */
  function isMobile() {
    return window.innerWidth <= 768;
  }

  /* ── Event Delegation ── */
  function init() {

    /* Vendor trigger (both desktop grid + mobile strip) */
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-vendor-trigger]');
      if (trigger) {
        var vendorId = trigger.getAttribute('data-vendor-id');
        if (isMobile()) {
          dismissStripHint();
          openSheet(vendorId);
        } else {
          activateVendor(vendorId);
        }
        return;
      }

      /* Member ID copy */
      var memberCopy = e.target.closest('[data-member-id-copy]');
      if (memberCopy) {
        e.preventDefault();
        copyMemberId(memberCopy);
        return;
      }

      /* Reveal trigger */
      var revealBtn = e.target.closest('[data-reveal-trigger]');
      if (revealBtn) {
        revealCode(revealBtn);
        return;
      }

      //* Email dispatch trigger */
      var dispatchBtn = e.target.closest('[data-dispatch-trigger]');
      if (dispatchBtn) {
        handleDispatch(dispatchBtn);
        return;
      }

      /* Member product offer: add to cart */
      var cartBtn = e.target.closest('[data-cart-trigger]');
      if (cartBtn) {
        e.preventDefault();
        handleCartAdd(cartBtn);
        return;
      }

      /* Copy button */
      var copyBtn = e.target.closest('[data-copy-btn]');
      if (copyBtn) {
        e.preventDefault();
        handleCopy(copyBtn);
        return;
      }

      /* Sheet backdrop close */
      var backdrop = e.target.closest('[data-sheet-backdrop]');
      if (backdrop) {
        closeSheet();
        return;
      }

      /* Sheet close button */
      var closeBtn = e.target.closest('[data-sheet-close]');
      if (closeBtn) {
        closeSheet();
        return;
      }
    });

    /* Keyboard: Escape closes sheet */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var sheet = qs('[data-sheet]');
        if (sheet && sheet.classList.contains('rn-membership-perks__sheet--active')) {
          closeSheet();
        }
      }
    });

    /* Member ID */
    setupMemberId();

    /* Restore email-sent state on desktop dispatch buttons */
    applyDispatchSentState(document);

    /* Mobile tap hint */
    setupStripHint();

    /* Mobile sticky strip collapse */
    setupStickyCollapse();

    /* Mobile infinite loop strip */
    setupInfiniteLoop();
  }

  /* Boot */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();